import React from 'react';
import { AlertTriangle, CheckCircle2, CircleSlash, Clock, Loader2, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

export const errorText = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

export const usd = (value, digits = 2) => (value == null ? '-' : `$${Number(value).toLocaleString(undefined, {
  minimumFractionDigits: digits, maximumFractionDigits: digits,
})}`);

export const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString(undefined, {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
}) : '-');

// Status always carries an icon + label, never colour alone
const STATUS = {
  ok: { label: 'Healthy', icon: CheckCircle2, className: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  failing: { label: 'Failing', icon: AlertTriangle, className: 'bg-amber-50 text-amber-800 ring-amber-200' },
  retiring: { label: 'Retiring', icon: Clock, className: 'bg-amber-50 text-amber-800 ring-amber-200' },
  gone: { label: 'Unavailable', icon: XCircle, className: 'bg-red-50 text-red-700 ring-red-200' },
  account: { label: 'Account issue', icon: AlertTriangle, className: 'bg-red-50 text-red-700 ring-red-200' },
  not_configured: { label: 'No API key', icon: CircleSlash, className: 'bg-slate-50 text-slate-600 ring-slate-200' },
  auto_disabled: { label: 'Auto-disabled', icon: XCircle, className: 'bg-red-50 text-red-700 ring-red-200' },
  unknown: { label: 'Not checked', icon: Clock, className: 'bg-slate-50 text-slate-600 ring-slate-200' },
  critical: { label: 'Critical', icon: XCircle, className: 'bg-red-50 text-red-700 ring-red-200' },
  warning: { label: 'Warning', icon: AlertTriangle, className: 'bg-amber-50 text-amber-800 ring-amber-200' },
  info: { label: 'Info', icon: Clock, className: 'bg-slate-50 text-slate-600 ring-slate-200' },
};

export const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.unknown;
  const Icon = s.icon;
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${s.className}`}>
      <Icon className="h-3.5 w-3.5" />{s.label}
    </span>
  );
};

/** One card per topic: title, optional description and header actions, then content. */
export const Section = ({ title, description, actions, children, footer, contentClassName = '' }) => (
  <Card className="border-slate-200 shadow-sm">
    <CardHeader className="flex flex-col gap-3 space-y-0 border-b border-slate-100 pb-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="space-y-1">
        <CardTitle className="text-base font-semibold text-slate-900">{title}</CardTitle>
        {description && <CardDescription className="text-sm text-slate-500">{description}</CardDescription>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </CardHeader>
    <CardContent className={`pt-4 ${contentClassName}`}>{children}</CardContent>
    {footer && <div className="border-t border-slate-100 px-6 py-4">{footer}</div>}
  </Card>
);

/** Plain table with consistent header, row and cell spacing. `align: 'right'` for numbers. */
export const DataTable = ({ columns, rows, rowKey, empty = 'Nothing to show yet' }) => (
  <div className="-mx-6 overflow-x-auto">
    <table className="w-full min-w-[640px] text-sm">
      <thead>
        <tr className="border-b border-slate-100 text-xs font-medium uppercase tracking-wide text-slate-500">
          {columns.map((col) => (
            <th key={col.key} className={`whitespace-nowrap px-6 py-2.5 font-medium ${col.align === 'right' ? 'text-right' : 'text-left'} ${col.className || ''}`}>
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((row, index) => (
          <tr key={rowKey ? rowKey(row) : index} className="hover:bg-slate-50/60">
            {columns.map((col) => (
              <td key={col.key} className={`px-6 py-3 align-middle ${col.align === 'right' ? 'text-right tabular-nums' : ''} ${col.className || ''}`}>
                {col.render ? col.render(row) : row[col.key]}
              </td>
            ))}
          </tr>
        ))}
        {rows.length === 0 && (
          <tr><td colSpan={columns.length} className="px-6 py-8 text-center text-slate-500">{empty}</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

export const StatTile = ({ label, value, hint }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
    <p className="mt-2 text-2xl font-semibold tabular-nums text-slate-900">{value}</p>
    {hint && <p className="mt-1 truncate text-xs text-slate-500" title={typeof hint === 'string' ? hint : undefined}>{hint}</p>}
  </div>
);

/** A labelled group of stat tiles. */
export const StatGroup = ({ title, children }) => (
  <div className="space-y-3">
    <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">{children}</div>
  </div>
);

/** Segmented control (e.g. date range, breakdown dimension). */
export const Segmented = ({ options, value, onChange, label }) => (
  <div className="inline-flex rounded-lg bg-slate-100 p-1" role="group" aria-label={label}>
    {options.map((opt) => (
      <button key={opt.value} type="button" onClick={() => onChange(opt.value)} aria-pressed={value === opt.value}
        className={`rounded-md px-3 py-1.5 text-sm transition-colors ${value === opt.value
          ? 'bg-white font-medium text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
        {opt.label}
      </button>
    ))}
  </div>
);

export const Loading = () => (
  <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
);
